import { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { CreditCard, Bitcoin, Landmark, Loader2, Copy, Upload, CheckCircle2, ArrowLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { clsx } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };
const opt = (active) => clsx("text-left rounded-lg border p-3 transition-colors", active ? "bg-brandblue/15 border-brandblue" : "bg-fx-base border-white/10 hover:border-white/25");

function CopyRow({ label, value, testid }) {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between gap-2 py-1.5 border-b border-white/5 last:border-0">
      <span className="text-xs text-fx-s3">{label}</span>
      <span className="flex items-center gap-1.5 min-w-0"><span className="font-mono text-xs text-white truncate" data-testid={testid}>{value}</span>
        <button onClick={() => copy(value)} className="text-fx-s3 hover:text-white shrink-0"><Copy className="h-3 w-3" /></button></span>
    </div>
  );
}

function Amount({ value, onChange, min, max }) {
  return (
    <div className="space-y-1">
      <Label>Amount (USD)</Label>
      <Input type="number" min={min} value={value} onChange={(e) => onChange(e.target.value)} data-testid="deposit-amount-input" className="bg-fx-base border-white/10 font-mono" />
      <p className="text-[11px] text-fx-s3" data-testid="deposit-limits">Minimum ${min}{max ? ` · Maximum $${max}` : ""}</p>
    </div>
  );
}

function CryptoForm({ m, f, setF, min }) {
  return (
    <div className="space-y-3">
      <div className="rounded-lg bg-fx-base border border-white/5 p-3 flex gap-3" data-testid="crypto-address-box">
        <div className="bg-white p-1.5 rounded shrink-0" data-testid="crypto-qr"><QRCodeSVG value={m.address} size={104} /></div>
        <div className="min-w-0 flex-1">
          <div className="text-xs text-fx-s3">Send only <b className="text-white">{m.coin}</b> on the <b className="text-white">{m.network}</b> network to:</div>
          <div className="text-xs font-mono break-all text-white mt-1" data-testid="crypto-address">{m.address}</div>
          <Button size="sm" variant="outline" onClick={() => copy(m.address)} data-testid="crypto-copy-address" className="mt-2 h-7 border-white/10 bg-fx-s2 gap-1"><Copy className="h-3 w-3" /> Copy address</Button>
        </div>
      </div>
      {/demo/i.test(m.address) && <p className="rounded-lg bg-neg/10 border border-neg/30 p-2 text-[11px] text-neg" data-testid="crypto-placeholder-warning">This is a placeholder demo address. Do NOT send real crypto until an admin sets a live receiving address.</p>}
      <p className="text-[11px] text-gold">Sending any other coin or using a different network may result in permanent loss of funds.</p>
      {m.instructions && <p className="text-xs text-fx-s3 whitespace-pre-line">{m.instructions}</p>}
      <Amount value={f.amount} onChange={(v) => setF({ ...f, amount: v })} min={Math.max(min, m.min_amount || 0)} max={m.max_amount} />
      <div className="space-y-1">
        <Label>Transaction hash</Label>
        <Input value={f.tx} onChange={(e) => setF({ ...f, tx: e.target.value })} placeholder="Paste your on-chain transaction hash" data-testid="crypto-txhash-input" className="bg-fx-base border-white/10 font-mono" />
      </div>
    </div>
  );
}

function BankForm({ m, f, setF, min }) {
  const [up, setUp] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("File too large (max 8MB)");
    setUp(true);
    try {
      const fd = new FormData(); fd.append("doc_type", "deposit_proof"); fd.append("file", file);
      const { data } = await api.post("/kyc/upload", fd);
      setF({ ...f, proof: data.file_id, proofName: file.name }); toast.success("Proof uploaded");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
    setUp(false);
  };
  return (
    <div className="space-y-3">
      <div className="rounded-lg bg-fx-base border border-white/5 px-3 py-1" data-testid="bank-details-box">
        <CopyRow label="Bank" value={m.bank_name} testid="bank-detail-name" />
        <CopyRow label="Account name" value={m.account_name} testid="bank-detail-account-name" />
        <CopyRow label="Account number" value={m.account_number} testid="bank-detail-account-number" />
        <CopyRow label="Routing / Sort code" value={m.routing_code} testid="bank-detail-routing" />
        <CopyRow label="SWIFT / BIC" value={m.swift} />
        <CopyRow label="Currency" value={m.currency} />
      </div>
      {m.instructions && <p className="text-xs text-fx-s3 whitespace-pre-line">{m.instructions}</p>}
      <Amount value={f.amount} onChange={(v) => setF({ ...f, amount: v })} min={Math.max(min, m.min_amount || 0)} max={m.max_amount} />
      <div className="space-y-1">
        <Label>Transfer reference</Label>
        <Input value={f.ref} onChange={(e) => setF({ ...f, ref: e.target.value })} placeholder="Reference / transaction ID from your bank" data-testid="bank-reference-input" className="bg-fx-base border-white/10 font-mono" />
      </div>
      <label className="flex items-center gap-2 rounded-lg border border-dashed border-white/15 px-3 py-2.5 text-xs text-fx-s3 cursor-pointer hover:border-white/30" data-testid="bank-proof-upload">
        {up ? <Loader2 className="h-4 w-4 animate-spin" /> : f.proof ? <CheckCircle2 className="h-4 w-4 text-pos" /> : <Upload className="h-4 w-4" />}
        <span className="truncate">{f.proof ? f.proofName : "Upload proof of payment (optional, image or PDF)"}</span>
        <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="bank-proof-input" />
      </label>
    </div>
  );
}

export default function DepositDialog({ open, onOpenChange, onDone }) {
  const [cfg, setCfg] = useState({ packages: [], min_deposit_usd: 10 });
  const [methods, setMethods] = useState({ crypto: [], bank: [], processors: [] });
  const [sel, setSel] = useState(null);
  const [pkg, setPkg] = useState("50");
  const [f, setF] = useState({ amount: 50, tx: "", ref: "", proof: null, proofName: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSel(null); setF({ amount: 50, tx: "", ref: "", proof: null, proofName: "" });
    api.get("/payments/packages").then((r) => setCfg(r.data));
    api.get("/payments/methods").then((r) => setMethods(r.data));
  }, [open]);

  const all = [
    ...methods.processors.map((p) => ({ key: `api-${p.provider}`, kind: "api", m: p, icon: CreditCard, title: p.provider === "stripe" ? `Card (${p.display_name})` : p.display_name, sub: "Instant" })),
    ...methods.crypto.map((m) => ({ key: m.id, kind: "crypto", m, icon: Bitcoin, title: m.label || m.coin, sub: m.network })),
    ...methods.bank.map((m) => ({ key: m.id, kind: "bank", m, icon: Landmark, title: m.label || m.bank_name, sub: "Bank transfer" })),
  ];
  const cur = all.find((x) => x.key === sel);
  const min = cfg.min_deposit_usd || 10;
  const pkgs = cur?.kind === "api" ? cfg.packages.filter((p) => (!cur.m.min_amount || p.amount >= cur.m.min_amount) && (!cur.m.max_amount || p.amount <= cur.m.max_amount)) : [];
  useEffect(() => { if (pkgs.length && !pkgs.some((p) => p.id === pkg)) setPkg(pkgs[0].id); }, [pkgs, pkg]);

  const submit = async () => {
    setBusy(true);
    try {
      if (cur.kind === "api") {
        const path = cur.m.provider === "stripe" ? "/payments/checkout" : `/payments/${cur.m.provider}/init`;
        const { data } = await api.post(path, { package_id: pkg, origin_url: window.location.origin });
        window.location.href = data.checkout_url; return;
      }
      const lo = Math.max(min, cur.m.min_amount || 0);
      if (!(Number(f.amount) >= lo)) throw new Error(`Minimum deposit is $${lo}`);
      if (cur.m.max_amount && Number(f.amount) > cur.m.max_amount) throw new Error(`Maximum deposit is $${cur.m.max_amount}`);
      if (cur.kind === "crypto") {
        if (f.tx.trim().length < 6) throw new Error("Enter your transaction hash");
        await api.post("/payments/crypto/deposit", { method_id: cur.m.id, amount_usd: Number(f.amount), tx_hash: f.tx });
      } else {
        if (f.ref.trim().length < 3) throw new Error("Enter your transfer reference");
        await api.post("/payments/bank/deposit", { method_id: cur.m.id, amount_usd: Number(f.amount), reference: f.ref, proof_file_id: f.proof });
      }
      toast.success("Deposit submitted, awaiting admin verification");
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e.response ? formatApiErrorDetail(e.response.data?.detail) : e.message || "Could not start deposit"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-fx-s2 border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto" data-testid="deposit-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2">
          {cur && <button onClick={() => setSel(null)} data-testid="deposit-back-btn" className="text-fx-s3 hover:text-white"><ArrowLeft className="h-4 w-4" /></button>}
          {cur ? cur.title : "Deposit to Live Wallet"}
        </DialogTitle></DialogHeader>
        {!cur ? (
          <div className="space-y-2" data-testid="deposit-methods">
            <Label>Choose a payment method</Label>
            {all.length === 0 && <p className="text-sm text-fx-s3 py-6 text-center" data-testid="deposit-no-methods">No deposit methods are available right now. Please contact support.</p>}
            <div className="grid grid-cols-2 gap-2">
              {all.map((x) => (
                <button key={x.key} onClick={() => setSel(x.key)} data-testid={`deposit-method-${x.kind}-${x.kind === "api" ? x.m.provider : x.m.id}`} className={opt(false)}>
                  <x.icon className="h-4 w-4 text-brandblue mb-1.5" />
                  <div className="text-sm font-semibold truncate">{x.title}</div>
                  <div className="text-[11px] text-fx-s3 truncate">{x.sub}</div>
                </button>
              ))}
            </div>
          </div>
        ) : cur.kind === "api" ? (
          <div className="space-y-2">
            <Label>Amount</Label>
            <div className="grid grid-cols-3 gap-2">
              {pkgs.map((p) => (
                <button key={p.id} onClick={() => setPkg(p.id)} data-testid={`package-${p.id}`} className={clsx("py-2 rounded-lg border font-mono text-sm transition-colors", pkg === p.id ? "bg-brandblue text-white border-brandblue" : "bg-fx-base border-white/10 text-fx-s3 hover:text-white")}>${p.amount}</button>
              ))}
            </div>
            {pkgs.length === 0 && <p className="text-xs text-neg" data-testid="deposit-no-packages">No amounts are available for this method.</p>}
            <p className="text-xs text-fx-s3">You'll be redirected to {cur.m.display_name} to complete payment securely. Your wallet is credited instantly.</p>
          </div>
        ) : cur.kind === "crypto" ? <CryptoForm m={cur.m} f={f} setF={setF} min={min} /> : <BankForm m={cur.m} f={f} setF={setF} min={min} />}
        {cur && (
          <DialogFooter>
            <Button onClick={submit} disabled={busy || (cur.kind === "api" && !pkgs.length)} data-testid="deposit-confirm-btn" className="bg-gold hover:brightness-110 text-black gap-2 w-full sm:w-auto">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : cur.kind === "api" ? `Continue to ${cur.m.display_name}` : "I've sent the payment"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
